import { Injectable, signal } from '@angular/core';
import { EntityConfig, EntityType, SelectorOptions } from './entity-list-selector.types';

/**
 * Service for controlling the global entity list selector dialog
 * and managing entity-specific configurations.
 *
 * This is generic reusable infrastructure with no entity types wired up yet
 * in this baseline — add a case to getEntityConfig() (and a value to
 * EntityType in entity-list-selector.types.ts) for each entity your app
 * wants to offer through the picker. Each case needs a service exposing
 * createQuery() (see the RQS pattern in CLAUDE.md) plus the display/search
 * field names the dialog should use.
 */
@Injectable({ providedIn: 'root' })
export class EntityListSelectorService {
  /** Whether the dialog is currently open */
  isOpen = signal(false);

  /** Current entity type being selected */
  currentEntityType = signal<EntityType | null>(null);

  /** Current selector configuration */
  config = signal<SelectorOptions | null>(null);

  /** Promise resolver for returning selected items */
  private resolvePromise: ((value: any[]) => void) | null = null;

  /**
   * Get entity-specific configuration based on entity type
   */
  getEntityConfig(entityType: EntityType): EntityConfig {
    switch (entityType) {
      case 'users':
        // TODO: Implement when UserService is available
        throw new Error('User selection not yet implemented');
      case 'locations':
        // TODO: Implement when LocationService is available
        throw new Error('Location selection not yet implemented');
      default:
        throw new Error(`Unsupported entity type: ${entityType}`);
    }
  }

  /**
   * Open the entity selector dialog (generic method)
   * @template T The entity type being selected
   * @param entityType Which entity type to select
   * @param options Selector configuration options
   * @returns Promise that resolves with selected items
   */
  openSelector<T>(entityType: EntityType, options: SelectorOptions): Promise<T[]> {
    this.currentEntityType.set(entityType);
    this.config.set(options);
    this.isOpen.set(true);

    return new Promise<T[]>((resolve) => {
      this.resolvePromise = resolve;
    });
  }

  /**
   * Called by dialog when user confirms selection
   * @param selected The selected items
   */
  confirm(selected: any[]): void {
    if (this.resolvePromise) {
      this.resolvePromise(selected);
      this.resolvePromise = null;
    }
    this.close();
  }

  /**
   * Called by dialog when user cancels
   */
  cancel(): void {
    if (this.resolvePromise) {
      this.resolvePromise([]);
      this.resolvePromise = null;
    }
    this.close();
  }

  /**
   * Close the dialog and reset state
   */
  private close(): void {
    this.isOpen.set(false);
    this.currentEntityType.set(null);
    this.config.set(null);
  }
}
